import type React from 'react';

import { JsonSchemaInput } from '@ragu-components/json-schema-input';
import {
  OrdBulkUpdateFormButton,
  OrdFormList,
  OrdIconStandard,
  OrdReferenceInput,
  OrdTabbedForm,
  useFormType,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { ExtensionUpdateFields, dataSchema } from '@ragu-components/ord-types';
import { TabbedForm, TextInput, useRecordContext } from 'react-admin';

import { EmbeddedSipIdentity } from './EmbeddedSipIdentity';

import type { OrdTabbedFormProps } from '@ragu-components/ord-components';
import type { Extension } from '@ragu-components/ord-types';

/**
 * Renders a form for creating or editing an Extension.
 *
 * @param {OrdTabbedFormProps} props - The props for the ExtensionsForm component.
 * @return {React.ReactElement} The rendered ExtensionsForm component.
 */
export const ExtensionsForm = (props: OrdTabbedFormProps): React.ReactElement => {
  const { create } = useFormType();
  const createJsonSchema = { ...dataSchema.ExtensionCreate, additionalProperties: true };
  const editJsonSchema = { ...dataSchema.ExtensionUpdate, additionalProperties: true };
  const record = useRecordContext<Extension>();

  return (
    <OrdTabbedForm
      sanitizeFields
      replaceRelationships
      syncWithLocation={false}
      editJsonSchema={editJsonSchema}
      createJsonSchema={createJsonSchema}
      {...props}
    >
      <TabbedForm.Tab label='resources.extensions.operations.sections.details'>
        <OrdFormList icon={<OrdIconStandard name='numbers' />}>
          <TextInput
            source={ExtensionUpdateFields.number}
            isRequired={create}
            resettable
            helperText='resources.extensions.helperTexts.number'
          />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='alarm' />}>
          <JsonSchemaInput
            source={ExtensionUpdateFields.ringtime}
            helperText='resources.extensions.helperTexts.ringtime'
          />
        </OrdFormList>
        <OrdFormList icon={<OrdIconStandard name='person' />}>
          <OrdReferenceInput resource={Resources.users} source={ExtensionUpdateFields.user} isRequired={create} />
        </OrdFormList>
      </TabbedForm.Tab>
      <TabbedForm.Tab label='resources.sip_identities.name'>
        <EmbeddedSipIdentity record={record} type={create ? 'create' : 'edit'} />
      </TabbedForm.Tab>
    </OrdTabbedForm>
  );
};

/** Form for bulk editing extensions */
export const ExtensionsBulkEditForm = (): React.ReactElement => {
  const inputs = [
    {
      label: 'resources.extensions.fields.ringtime',
      element: (
        <OrdFormList icon={<OrdIconStandard name='alarm' />}>
          <JsonSchemaInput
            source={ExtensionUpdateFields.ringtime}
            jsonSchema={dataSchema.ExtensionUpdate}
            helperText='resources.extensions.helperTexts.ringtime'
          />
        </OrdFormList>
      ),
    },
    {
      label: 'resources.extensions.fields.user',
      element: (
        <OrdFormList icon={<OrdIconStandard name='person' />}>
          <OrdReferenceInput resource={Resources.users} source={ExtensionUpdateFields.user} />
        </OrdFormList>
      ),
    },
  ];
  return <OrdBulkUpdateFormButton inputs={inputs} />;
};
